import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * The open drawers of the analysis workbench, kept in the address
 * (docs/ANALYSIS_WORKBENCH.md section 5): ?drawers=source,threshold, in the
 * order they were opened. So Back closes the last drawer opened, and a link
 * or a reload opens the same ones.
 *
 * Opening a drawer pushes a history entry whose state names it
 * (`state.wbDrawer`). Closing the drawer that entry names goes back through
 * it; closing any other (or one whose entry was pushed over by something
 * else, a new photograph) replaces the address without it, and forgets the
 * entry's own drawer, whose close then replaces too. Several hooks
 * keeping one sheet each (hooks/useAddressSheet.js) would go back through the
 * wrong entry when a drawer opened earlier is closed first, which is why the
 * workbench keeps them in one param.
 */
export const DRAWERS_PARAM = 'drawers';
export const STATE_KEY = 'wbDrawer';

/** The drawer ids named in a query string (or URLSearchParams), in order, once each. */
export function parseDrawers(search) {
  const query = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  const raw = query.get(DRAWERS_PARAM) || '';
  return [...new Set(raw.split(',').map(s => s.trim()).filter(id => /^[A-Za-z0-9_-]{1,40}$/.test(id)))];
}

/** A query (URLSearchParams) with `list` as its drawers, or none. */
export function withDrawers(query, list) {
  const out = new URLSearchParams(query);
  if (list.length) out.set(DRAWERS_PARAM, list.join(','));
  else out.delete(DRAWERS_PARAM);
  return out;
}

const stateWithout = (state) => {
  if (!state || typeof state !== 'object') return null;
  const rest = { ...state };
  delete rest[STATE_KEY];
  return rest;
};

export default function useDrawerAddress() {
  const location = useLocation();
  const navigate = useNavigate();
  const list = useMemo(() => parseDrawers(location.search), [location.search]);
  const pushed = location.state && typeof location.state === 'object' ? location.state[STATE_KEY] : undefined;

  const go = useCallback((next, { replace, state }) => {
    const search = withDrawers(new URLSearchParams(location.search), next).toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace, state });
  }, [location.pathname, location.search, location.hash, navigate]);

  const open = useCallback((id, { replace = false } = {}) => {
    if (list.includes(id)) return;
    const next = [...list, id];
    if (replace) go(next, { replace: true, state: location.state ?? null });
    else go(next, { replace: false, state: { ...(stateWithout(location.state) || {}), [STATE_KEY]: id } });
  }, [list, go, location.state]);

  const close = useCallback((id) => {
    if (!list.includes(id)) return false;
    if (pushed === id) {
      navigate(-1);
      return true;
    }
    // Out of order: the entries under this one still show it open, so the
    // drawer this entry pushed is no longer one Back can close cleanly either.
    go(list.filter(x => x !== id), { replace: true, state: stateWithout(location.state) });
    return true;
  }, [list, pushed, go, navigate, location.state]);

  return { list, open, close };
}

/**
 * The same {list, open, close}, kept in memory: the drawers of a workbench
 * shown over another page (WorkbenchShell's `overlay` variant, Field Mode's
 * annotate). It has no address of its own, so it writes none: the page
 * beneath keeps its address, and its Back stays the page's. Opened in order,
 * closed in any order; `close` says whether the drawer was open.
 */
export function useDrawerMemory() {
  const [list, setList] = useState([]);
  const open = useCallback((id) => {
    setList(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);
  const close = useCallback((id) => {
    if (!list.includes(id)) return false;
    setList(prev => prev.filter(x => x !== id));
    return true;
  }, [list]);
  return { list, open, close };
}
